import React, { FC, useEffect, useState, ChangeEvent } from 'react';
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Button,
    Box,
    Grid,
    Paper,
    TextField,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import NumberOnlyTextField from 'components/number-only-text-field/number-only-text-field';
import TransportStatus from './transport-status.component';
import { Organization } from '../../../models/organization';
import { transportDateHelper } from 'utils/transport-date-helper';
import TransportLog from './transport-log.component';
import moment from 'moment';
import { TransportLocation, TransportOrder, TransportServices } from 'models/transport';
import { TransportStatus as TransportStatusEnum } from 'models/transport';
import TransportPoint from 'components/transport/shared/transport-point.component';

type TransportOrderDetailsDialogProps = {
    open: boolean;
    handleClose: () => void;
    handleOk: (transport: TransportOrder) => void;
    transport: TransportOrder | undefined;
};

const TransportOrderDetailsDialog: FC<TransportOrderDetailsDialogProps> = ({
    open,
    handleOk,
    handleClose,
    transport,
}: TransportOrderDetailsDialogProps) => {
    const { t: translate } = useTranslation(['transport']);
    const [pickupOrganization, setPickupOrganization] = useState<Organization>({} as Organization);
    const [dropoffOrganization, setDropoffOrganization] = useState<Organization>({} as Organization);
    const [pickUpDate, setPickUpDate] = useState<string>('');
    const [dropoffDate, setDropoffDate] = useState<string>('');
    const [noOfManualPackages, setNoOfManualPackages] = useState<number>(0);
    const [comments, setComments] = useState<string>();

    const dialogTitle = (transport: TransportOrder | undefined) => {
        if (transport) {
            const transportType = translate(`transport:${transport.serviceCode}`);
            return `${transportType} - ${transport.externalReference}`;
        }
        return '';
    };

    useEffect(() => {
        if (transport) {
            setPickupOrganization(
                transport.serviceCode === TransportServices.PICKUP
                    ? (transport.clinic as Organization)
                    : (transport.lab as Organization),
            );
            setDropoffOrganization(
                transport.serviceCode === TransportServices.PICKUP
                    ? (transport.lab as Organization)
                    : (transport.clinic as Organization),
            );
            setPickUpDate(moment(transport.transportDate).format('YYYY-MM-DD'));
            setDropoffDate(transportDateHelper.nextWorkingDayAsString(transport.transportDate));
            setNoOfManualPackages(transport.manualPackageCount as number);
            setComments(transport.comment);
        }
    }, [transport]);

    const onPackagesChange = (packages: string) => {
        const value = parseInt(packages);
        setNoOfManualPackages(value);
    };

    const onCommentChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
        setComments(event.target.value);
    };

    const updateTransport = () => {
        const transportObj = Object.assign({}, transport, {
            manualPackageCount: noOfManualPackages,
            comments: comments,
        });
        handleOk(transportObj);
    };

    const getTransportLocation = (org: Organization): TransportLocation => {
        return {
            organizationId: org.organizationNumber,
            name: org.name,
            streetAddress: org.contact?.address?.street || '',
            zipCode: org.contact?.address?.postalCode || '',
            city: org.contact?.address?.city || '',
            countryCode: org.contact?.address?.country,
            deliveryInstructions: '',
            phoneNumber: org.contact?.communication?.phone,
            emailAddress: org.contact?.communication?.email,
        };
    };

    return (
        <Dialog fullWidth maxWidth="xl" open={open} onClose={handleClose}>
            <DialogTitle>{dialogTitle(transport)}</DialogTitle>
            <DialogContent>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <Box>
                            <TransportStatus
                                status={transport?.status || TransportStatusEnum.PLANNED}
                                hasProblem={transport?.hasProblem}
                            />
                        </Box>
                        <Box mt={2}>
                            <Paper variant="outlined">
                                <Box p={1}>
                                    <TransportPoint
                                        location={getTransportLocation(pickupOrganization)}
                                        isFrom={true}
                                        date={pickUpDate}
                                    />
                                    <TransportPoint
                                        location={getTransportLocation(dropoffOrganization)}
                                        isFrom={false}
                                        date={dropoffDate}
                                    />
                                </Box>
                            </Paper>
                        </Box>
                        {transport?.orders && transport.orders.length > 0 ? (
                            <Box mt={2}>
                                <Paper variant="outlined">
                                    <Box p={1}>
                                        <Typography variant="h3">
                                            {translate('transport:DENTHUB_ORDERS_TO_PICKUP')}
                                        </Typography>
                                    </Box>
                                    <ul>
                                        {transport?.orders?.map((order) => (
                                            <li key={order._id}>
                                                <Typography variant="caption">
                                                    {order.orderId} - {order.patient?.firstName}{' '}
                                                    {order.patient?.lastName}, {order.patient?.personalIdentityNumber}
                                                </Typography>
                                            </li>
                                        ))}
                                    </ul>
                                </Paper>
                            </Box>
                        ) : undefined}
                        <Box mt={2}>
                            <NumberOnlyTextField
                                onValueChange={onPackagesChange}
                                size="small"
                                fullWidth
                                label={translate('transport:NO_OF_ADDITIONAL_PACKAGES')}
                                hasDecimal={false}
                                value={noOfManualPackages}
                            />
                        </Box>
                        <Box mt={2}>
                            <TextField
                                label={translate(`transport:MESSAGE_TO_CARRIER`)}
                                size="small"
                                fullWidth
                                value={comments}
                                onChange={onCommentChange}
                                multiline
                                rows={3}
                            />
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TransportLog opterOrderId={transport?.externalReference} />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>{translate('common:CANCEL')}</Button>
                <Button onClick={updateTransport} variant="contained">
                    {translate('common:SAVE')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default TransportOrderDetailsDialog;
